import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BsRobot, BsLightningCharge, BsClipboardData, BsGraphUpArrow } from 'react-icons/bs';
import { HiOutlineSparkles } from 'react-icons/hi2';

const features = [
  {
    icon: BsRobot,
    title: 'AI Tutor',
    description: 'Get instant, step-by-step explanations for any question. Ask in text or voice — our AI understands your syllabus and teaches like a personal coach.',
    gradient: 'from-brand-500 to-blue-600',
    shadow: 'shadow-brand-500/20',
    link: '/mode?category=school',
  },
  {
    icon: BsLightningCharge,
    title: 'Smart Practice',
    description: 'Drill into previous year board and university papers with guided selection. Choose board, grade, subject, year, and term — then practice.',
    gradient: 'from-accent-500 to-purple-600',
    shadow: 'shadow-accent-500/20',
    link: '/mode?category=school',
  },
  {
    icon: BsClipboardData,
    title: 'Mock Exams',
    description: 'Generate custom question papers in seconds. Pick your syllabus, subject, chapters, and difficulty — AI builds a real exam-format paper for you.',
    gradient: 'from-emerald-500 to-teal-600',
    shadow: 'shadow-emerald-500/20',
    link: '/mode?category=school',
  },
  {
    icon: BsGraphUpArrow,
    title: 'Frequently Asked',
    description: 'See which questions appear most often across years. Our frequency analysis highlights high-yield topics so you study what matters most.',
    gradient: 'from-orange-500 to-red-600',
    shadow: 'shadow-orange-500/20',
    link: '/mode?category=school',
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

export default function Features() {
  return (
    <section id="features" className="section-padding relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-brand-600/10 rounded-full blur-[120px]" />

      <div className="container-custom mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-6">
            <HiOutlineSparkles className="text-accent-400" />
            <span className="text-sm font-medium text-gray-300">Why ZenQB</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-white mb-6">
            Study with AI superpowers
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Everything you need to ace your exams — powered by artificial intelligence and built on real board and university question papers.
          </p>
        </motion.div>

        {/* Feature Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {features.map((feature, i) => (
            <motion.div
              key={i}
              variants={cardVariants}
              className="group relative"
            >
              <Link to={feature.link} className="block">
                <div className={`glass rounded-3xl p-8 md:p-10 h-full hover:bg-white/[0.08] transition-all duration-500 hover:shadow-2xl ${feature.shadow} hover:-translate-y-1`}>
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="text-2xl text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold font-display text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-[15px]">{feature.description}</p>

                  {/* Hover gradient border effect */}
                  <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
